import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Table } from "../../../components/SystemComponent/table";
import { GroupButton } from "../../../components/SystemComponent/GroupButton";
import { Badge } from "../../../components/SystemComponent/Bagde";
import { Modal } from "../../../components/SystemComponent/Modal";
import { Button } from "../../../components/SystemComponent/Button";
import { MdAdd, MdEdit, MdDelete } from "react-icons/md";
import { createApi } from "../../../../core/api";

const MediaOwnerType = { Banner: 13 };
const MediaType = { Image: 1 };

export const BannerCrud = () => {
  const api = useMemo(() => createApi(), []);
  const { setPageMenus } = useOutletContext<any>();

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);

  const [code, setCode] = useState("");
  const [note, setNote] = useState("");
  const [redirectUrl, setRedirectUrl] = useState("");
  const [expireTime, setExpireTime] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [currentImage, setCurrentImage] = useState<any>(null);

  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.banner
      .getAll()
      .then((r: any) => {
        if (r?.ok) {
          const list = r.data?.data ?? r.data ?? [];
          setData(Array.isArray(list) ? list : []);
        }
      })
      .finally(() => setLoading(false));
  }, [api]);

  useEffect(() => {
    setPageMenus([
      {
        label: "Banners",
        items: [{ label: "Banners", route: "/dashboard/banner" }],
      },
    ]);
    load();
    return () => setPageMenus([]);
  }, [load, setPageMenus]);

  const openCreate = () => {
    setEditing(null);
    setCode("");
    setNote("");
    setRedirectUrl("");
    setExpireTime("");
    setImageFile(null);
    setCurrentImage(null);
    setError(null);
    setOpen(true);
  };

  const openEdit = async (row: any) => {
    setEditing(row);
    setCode(row.code ?? "");
    setNote(row.note ?? "");
    setRedirectUrl(row.redirectUrl ?? "");
    setExpireTime(row.expireTime ? row.expireTime.slice(0, 16) : "");
    setImageFile(null);
    setError(null);
    // row.image = { id, url } objesi — bunu currentImage olarak saklıyoruz
    // böylece yeni dosya seçilince api.media.update(currentImage.id, ...) çalışır
    setCurrentImage(row.image ?? null);
    setOpen(true);
  };

  const submit = async () => {
    if (saving) return;

    const isCreate = !editing;
    const hasImage = !!currentImage || !!imageFile;

    if (!code.trim()) {
      setError("Code is required");
      return;
    }

    if (!expireTime) {
      setError("Expire date is required");
      return;
    }

    if (isCreate && !imageFile) {
      setError("Image is required");
      return;
    }

    if (!hasImage) {
      setError("Banner must have an image");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      let bannerId = editing?.id;

      if (editing) {
        await api.banner.update(bannerId, {
          code,
          note,
          redirectUrl,
          expireTime,
        });
      } else {
        const r: any = await api.banner.create({
          code,
          note,
          redirectUrl,
          expireTime,
        });
        bannerId = r?.data?.data ?? r?.data;
      }

      if (!bannerId) throw new Error();

      if (imageFile) {
        if (currentImage?.id) {
          await api.media.update(currentImage.id, {
            ownerType: MediaOwnerType.Banner,
            mediaType: MediaType.Image,
            isPrimary: true,
            file: imageFile,
          });
        } else {
          await api.media.uploadSingle({
            file: imageFile,
            ownerType: MediaOwnerType.Banner,
            ownerId: bannerId,
            mediaType: MediaType.Image,
            isPrimary: true,
          });
        }
      }

      setOpen(false);
      load();
    } catch {
      setError("Operation failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!deleteId || deleting) return;
    setDeleting(true);
    await api.banner.delete(deleteId);
    setDeleteId(null);
    setDeleting(false);
    load();
  };

  const columns = [
    { key: "id", title: "ID", type: "number", minWidth: 80, accessor: (r: any) => r.id },
    {
      key: "image",
      title: "Image",
      filterable: false,
      minWidth: 120,
      render: (row: any) =>
        row.image?.url ? (
          <img
            src={row.image.url}
            style={{
              width: 60,
              height: 40,
              objectFit: "cover",
              borderRadius: 6,
              cursor: "pointer",
            }}
            onClick={() => setPreviewImage(row.image.url)}
          />
        ) : (
          "-"
        ),
    },
    {
      key: "code",
      title: "Code",
      type: "text",
      minWidth: 200,
      accessor: (r: any) => r.code,
      render: (_: any, v: string) => (
        <Badge label={v} color="#2563EB" bg="rgba(37,99,235,.15)" />
      ),
    },
    {
      key: "expire",
      title: "Expire",
      type: "date",
      minWidth: 160,
      accessor: (r: any) => r.expireTime,
      render: (_: any, v: any) => (v ? new Date(v).toLocaleDateString() : "-"),
    },
    {
      key: "click",
      title: "Clicks",
      type: "number",
      minWidth: 120,
      accessor: (r: any) => r.clickCount ?? 0,
    },
    {
      key: "actions",
      title: "Actions",
      minWidth: 200,
      render: (row: any) => (
        <GroupButton
          items={[
            {
              key: "edit",
              icon: MdEdit,
              color: "#2563EB",
              onClick: () => openEdit(row),
            },
            {
              key: "delete",
              icon: MdDelete,
              color: "#DC2626",
              onClick: () => setDeleteId(row.id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div>
          <div style={S.title}>Banners</div>
          <div style={S.subTitle}>Manage banners</div>
        </div>
        <Button icon={MdAdd} onClick={openCreate}>
          Create Banner
        </Button>
      </div>

      <Table
        tableKey="banner"
        loading={loading}
        columns={columns}
        data={data}
        features={{ sortable: true, filterable: true, pagination: true }}
      />

      <Modal open={open} onClose={() => (!saving ? setOpen(false) : null)}>
        <div style={S.modalTitle}>
          {editing ? "Update Banner" : "Create Banner"}
        </div>

        <div style={S.form}>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Code *"
            style={S.input}
          />
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note"
            style={S.input}
          />
          <input
            value={redirectUrl}
            onChange={(e) => setRedirectUrl(e.target.value)}
            placeholder="Redirect Url"
            style={S.input}
          />
          <input
            type="datetime-local"
            value={expireTime}
            onChange={(e) => setExpireTime(e.target.value)}
            style={S.input}
          />

          {currentImage?.url && (
            <div style={{ position: "relative" }}>
              <div style={{ fontSize: 11, color: "#64748B", marginBottom: 4 }}>
                Mevcut fotoğraf — yeni seçim yaparsanız güncellenir
              </div>
              <img
                src={currentImage.url}
                style={{ width: "100%", borderRadius: 8, border: "2px solid #CBD5E1" }}
              />
            </div>
          )}

          <div style={{ fontSize: 12, color: "#374151", fontWeight: 600, marginTop: 4 }}>
            {editing ? "Yeni fotoğraf seç (opsiyonel):" : "Fotoğraf seç *:"}
          </div>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setImageFile(e.target.files?.[0] || null)}
            style={{ fontSize: 13 }}
          />

          {error && <div style={S.error}>{error}</div>}
        </div>

        <div style={S.actions}>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button loading={saving} onClick={submit}>
            Save
          </Button>
        </div>
      </Modal>

      <Modal open={!!deleteId} onClose={() => setDeleteId(null)}>
        <div style={S.modalTitle}>Delete Banner</div>
        <div>Are you sure?</div>
        <div style={S.actions}>
          <Button variant="ghost" onClick={() => setDeleteId(null)}>
            Cancel
          </Button>
          <Button variant="danger" loading={deleting} onClick={doDelete}>
            Delete
          </Button>
        </div>
      </Modal>

      <Modal
        fullScreen
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
      >
        <img
          src={previewImage ?? ""}
          style={{ maxWidth: "50vw", maxHeight: "50vh" }}
        />
      </Modal>
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  wrap: { position: "absolute", inset: 0, padding: 20, background: "#F8FAFC" },
  header: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  title: { fontSize: 18, fontWeight: 700 },
  subTitle: { fontSize: 13, color: "#64748B" },
  modalTitle: { fontSize: 18, fontWeight: 800, marginBottom: 14 },
  form: { display: "flex", flexDirection: "column", gap: 10 },
  input: {
    height: 42,
    borderRadius: 10,
    border: "1px solid #CBD5E1",
    padding: "0 12px",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 20,
  },
  error: { fontSize: 12, color: "#DC2626", fontWeight: 600 },
};
